import React from "react";
import {
  Paper,
  Stack,
  Typography,
  Box,
  LinearProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Chip,
  Tooltip,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import ParticipantCoverageFilters, {
  DatePreset,
} from "./ParticipantCoverageFilters";
import { DataCoverageService } from "../../../services/dataCoverageService";
import { apiService } from "../../../config/api";
import { COMMON_FITBIT_HEALTHKIT_METRIC_KEYS } from "../../../constants/dataCoverageCommonMetrics";
import {
  getMetricsForDeviceType,
  METRIC_LABELS,
  METRIC_CATEGORIES,
} from "../../../constants/metricCategories";
import type { StudyDeviceAvailability } from "./studyDeviceAvailability";

interface StudyLevelMetricDetailCardProps {
  studyId: string;
  deviceAvailability: StudyDeviceAvailability;
  onDataChange?: (data: StudyMetricDetailResult | null) => void;
}

interface ParticipantMetricData {
  participantId: string;
  participantName: string;
  coveragePct: number;
  daysWithData: number;
  values: number[];
  minValue: number | null;
  maxValue: number | null;
  meanValue: number | null;
}

interface MetricDistributionBucket {
  range: string;
  count: number;
  percentage: number;
}

interface MetricDetailAnalysis {
  metricKey: string;
  metricLabel: string;
  participantCount: number;
  participantsWithData: number;
  statistics: {
    minValue: number | null;
    maxValue: number | null;
    meanValue: number | null;
    avgCoveragePct: number;
  };
  participants: ParticipantMetricData[];
  coverageDistribution: MetricDistributionBucket[];
  lowestCoverageParticipants: ParticipantMetricData[];
}

interface StudyMetricDetailResult {
  study: {
    studyId: string;
    studyName: string;
    totalParticipants: number;
  };
  selectedDeviceType: "fitbit" | "healthkit" | "both";
  range: {
    fromDate: string;
    endDate: string;
    totalDays: number;
    preset: string | null;
  };
  analysis: MetricDetailAnalysis | null;
  participantsAnalyzed: number;
  fetch?: {
    source: string;
    statusMessage: string;
    refreshedFromApi: boolean;
    participantsWithMissingFitbitData: number;
    participantsRefreshedFromApi: number;
    participantsWithFailedFitbitRefresh: number;
    failedFitbitRefreshes: {
      participantId: string;
      participantName: string;
      ranges: { startDate: string; endDate: string; dayCount: number }[];
    }[];
  };
}

const StudyLevelMetricDetailCard: React.FC<StudyLevelMetricDetailCardProps> = ({
  studyId,
  deviceAvailability,
  onDataChange,
}) => {
  const [fromDate, setFromDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [preset, setPreset] = React.useState<DatePreset | null>("7d");
  const [deviceType, setDeviceType] = React.useState<
    "fitbit" | "healthkit" | "both"
  >("both");
  const [availableMetrics, setAvailableMetrics] = React.useState<
    { key: string; label: string }[]
  >([]);
  const [selectedMetric, setSelectedMetric] = React.useState<string | null>(
    null,
  );
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<StudyMetricDetailResult | null>(
    null,
  );
  const [progressStatus, setProgressStatus] = React.useState("");

  const hasFitbitParticipants = deviceAvailability.fitbitParticipants > 0;
  const hasHealthkitParticipants = deviceAvailability.healthkitParticipants > 0;
  const canSelectBoth = hasFitbitParticipants && hasHealthkitParticipants;

  React.useEffect(() => {
    if (deviceType === "fitbit" && !hasFitbitParticipants) {
      if (hasHealthkitParticipants) {
        setDeviceType("healthkit");
      } else {
        setDeviceType("both");
      }
      return;
    }

    if (deviceType === "healthkit" && !hasHealthkitParticipants) {
      if (hasFitbitParticipants) {
        setDeviceType("fitbit");
      } else {
        setDeviceType("both");
      }
      return;
    }

    if (deviceType === "both" && !canSelectBoth) {
      if (hasFitbitParticipants) {
        setDeviceType("fitbit");
      } else if (hasHealthkitParticipants) {
        setDeviceType("healthkit");
      }
    }
  }, [
    canSelectBoth,
    deviceType,
    hasFitbitParticipants,
    hasHealthkitParticipants,
  ]);

  // Initialize dates on mount
  React.useEffect(() => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    setFromDate(start);
    setEndDate(end);
  }, []);

  // Notify parent when data changes
  React.useEffect(() => {
    if (onDataChange) {
      onDataChange(result);
    }
  }, [result, onDataChange]);

  // Update available metrics when device type changes
  React.useEffect(() => {
    let metrics: { key: string; label: string }[] = [];

    if (deviceType === "both") {
      metrics = COMMON_FITBIT_HEALTHKIT_METRIC_KEYS.map((key) => ({
        key,
        label: METRIC_LABELS[key] || key,
      }));
    } else {
      const metricKeys = getMetricsForDeviceType(deviceType);
      metrics = metricKeys.map((key) => ({
        key,
        label: METRIC_LABELS[key] || key,
      }));
    }

    setAvailableMetrics(metrics);
    setSelectedMetric(metrics.length > 0 ? metrics[0].key : null);
    setResult(null);
  }, [deviceType]);

  const applyPreset = (nextPreset: DatePreset | null) => {
    setPreset(nextPreset);
    if (!nextPreset) return;

    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    let start = new Date(today);

    if (nextPreset === "7d") {
      start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
    } else if (nextPreset === "1m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 1);
      start.setDate(start.getDate() + 1);
    } else if (nextPreset === "6m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      start.setDate(start.getDate() + 1);
    } else {
      start = new Date(today);
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
    }

    setFromDate(start.toISOString().slice(0, 10));
    setEndDate(end);
  };

  const handleRun = async () => {
    if (!fromDate || !endDate) {
      setError("Please select both from and end dates.");
      return;
    }

    if (!selectedMetric) {
      setError("Please select a metric.");
      return;
    }

    try {
      setRunning(true);
      setError(null);
      setProgressStatus("Analyzing metric coverage...");
      setResult(null);

      const token = await apiService.getAuthToken();
      if (!token) {
        setError("Sign in to run analysis.");
        return;
      }

      const response = await DataCoverageService.getStudyMetricDetail(
        studyId,
        {
          fromDate,
          endDate,
          preset,
          deviceType,
          metricKey: selectedMetric,
          refreshIfMissing: true,
        },
        token,
      );

      if (response.success && (response as any).data) {
        setResult((response as any).data);
        setProgressStatus("");
      } else {
        setError("Failed to analyze metric coverage.");
      }
    } catch (err: any) {
      setError(err?.message || "Error analyzing metric.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Metric-wise Summary
        </Typography>

        {/* Controls */}
        <Stack spacing={2}>
          {/* Device Type Selector */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
              Device Type
            </Typography>
            <ToggleButtonGroup
              value={deviceType}
              exclusive
              onChange={(_, value) => {
                if (value) setDeviceType(value);
              }}
              size="small"
            >
              <ToggleButton value="fitbit" disabled={!hasFitbitParticipants}>
                Fitbit Only
              </ToggleButton>
              <ToggleButton
                value="healthkit"
                disabled={!hasHealthkitParticipants}
              >
                HealthKit Only
              </ToggleButton>
              <ToggleButton value="both" disabled={!canSelectBoth}>
                Both / Common Metrics
              </ToggleButton>
            </ToggleButtonGroup>

            {!hasFitbitParticipants && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5 }}
              >
                No participant with Fitbit is enrolled in this study.
              </Typography>
            )}
            {!hasHealthkitParticipants && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.25 }}
              >
                No participant with HealthKit is enrolled in this study.
              </Typography>
            )}
          </Box>

          {/* Metric Selector */}
          <FormControl fullWidth size="small">
            <InputLabel id="metric-select-label">Select Metric</InputLabel>
            <Select
              labelId="metric-select-label"
              value={selectedMetric || ""}
              label="Select Metric"
              onChange={(e) => setSelectedMetric(e.target.value)}
            >
              {(
                Object.entries(METRIC_CATEGORIES) as Array<
                  [string, readonly string[]]
                >
              ).flatMap(([category, metricKeys]) => {
                // Filter metrics in this category that are available
                const availableInCategory = metricKeys.filter((key) =>
                  availableMetrics.some((m) => m.key === key),
                );

                // Only show category if it has available metrics
                if (availableInCategory.length === 0) return [];

                return [
                  <ListSubheader
                    key={`${category}-header`}
                    sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}
                  >
                    {category}
                  </ListSubheader>,
                  ...availableInCategory.map((key) => (
                    <MenuItem key={key} value={key}>
                      {METRIC_LABELS[key] || key}
                    </MenuItem>
                  )),
                ];
              })}
            </Select>
          </FormControl>

          {/* Date Filters */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <ParticipantCoverageFilters
              disabled={false}
              fromDate={fromDate}
              endDate={endDate}
              preset={preset}
              onFromDateChange={(value) => {
                setPreset(null);
                setFromDate(value);
              }}
              onEndDateChange={(value) => {
                setPreset(null);
                setEndDate(value);
              }}
              onPresetChange={applyPreset}
              onRun={handleRun}
              running={running}
              selectedMetric={selectedMetric}
              requireMetricSelection={true}
            />
          </Box>
        </Stack>

        {/* Loading Progress */}
        {running && (
          <Stack spacing={1}>
            <LinearProgress variant="indeterminate" />
            <Typography variant="body2" color="textSecondary">
              {progressStatus || "Running..."}
            </Typography>
          </Stack>
        )}

        {/* Error */}
        {error && <Alert severity="error">{error}</Alert>}

        {result?.fetch?.statusMessage && (
          <Alert severity="info">{result.fetch.statusMessage}</Alert>
        )}

        {result?.fetch?.failedFitbitRefreshes &&
          result.fetch.failedFitbitRefreshes.length > 0 && (
            <Alert severity="warning">
              Fitbit data could not be retrieved for:{" "}
              {result.fetch.failedFitbitRefreshes
                .map((entry) => {
                  const ranges = entry.ranges
                    .map((range) => `${range.startDate} to ${range.endDate}`)
                    .join(", ");
                  return `${entry.participantName} (${ranges})`;
                })
                .join("; ")}
            </Alert>
          )}

        {/* Results */}
        {result && result.analysis && !running && (
          <Stack spacing={2.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {result.analysis.metricLabel} Analysis ({result.range.fromDate} to{" "}
              {result.range.endDate})
            </Typography>

            {/* Statistics Grid */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Participants Analyzed
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {result.analysis.participantsWithData}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    backgroundColor: "#e3f2fd",
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Avg Coverage
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {result.analysis.statistics.avgCoveragePct.toFixed(1)}%
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Mean Value
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {result.analysis.statistics.meanValue !== null
                      ? result.analysis.statistics.meanValue.toFixed(2)
                      : "-"}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Range
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {result.analysis.statistics.minValue !== null &&
                    result.analysis.statistics.maxValue !== null
                      ? `${result.analysis.statistics.minValue.toFixed(2)} - ${result.analysis.statistics.maxValue.toFixed(2)}`
                      : "-"}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Coverage Distribution */}
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Coverage Distribution Across Cohort
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {result.analysis.coverageDistribution.map((bucket, idx) => (
                  <Tooltip key={idx} title={`${bucket.count} participants`}>
                    <Chip
                      label={`${bucket.range}: ${bucket.percentage}%`}
                      variant="outlined"
                      size="small"
                    />
                  </Tooltip>
                ))}
              </Box>
            </Stack>

            {/* Low Coverage Participants */}
            {result.analysis.lowestCoverageParticipants.length > 0 && (
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <WarningIcon fontSize="small" sx={{ color: "#ff9800" }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Low Coverage Participants (Potential Adherence Issues)
                  </Typography>
                </Box>
                <TableContainer
                  component={Paper}
                  sx={{ maxHeight: "400px", overflowY: "auto" }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Participant
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Coverage %
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Days w/ Data
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          Mean Value
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.analysis.lowestCoverageParticipants.map(
                        (participant) => (
                          <TableRow key={participant.participantId}>
                            <TableCell>
                              <Typography variant="body2">
                                {participant.participantName}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color:
                                    participant.coveragePct < 50
                                      ? "#d32f2f"
                                      : "#ff9800",
                                }}
                              >
                                {participant.coveragePct.toFixed(1)}%
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">
                                {participant.daysWithData}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">
                                {participant.meanValue !== null
                                  ? participant.meanValue.toFixed(2)
                                  : "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )}

            {/* All Participants Data */}
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                All Participants ({result.analysis.participants.length})
              </Typography>
              <TableContainer
                component={Paper}
                sx={{ maxHeight: "600px", overflowY: "auto" }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Participant
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        Coverage %
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        Days w/ Data
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        Min
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        Mean
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        Max
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.analysis.participants.map((participant) => (
                      <TableRow key={participant.participantId}>
                        <TableCell>
                          <Typography variant="body2">
                            {participant.participantName}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {participant.coveragePct.toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {participant.daysWithData}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {participant.minValue !== null
                              ? participant.minValue.toFixed(2)
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {participant.meanValue !== null
                              ? participant.meanValue.toFixed(2)
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {participant.maxValue !== null
                              ? participant.maxValue.toFixed(2)
                              : "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default StudyLevelMetricDetailCard;
